import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Save, Trash2, Truck, UtensilsCrossed, CalendarDays, Star } from "lucide-react";
import { adminLogin, adminMe, adminSaveEvents, adminSaveMenu, adminSaveReviews, adminSaveSchedule, fetchEvents, fetchMenu, fetchReviews, fetchSchedule } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUSES = ["SERVING", "CLOSED", "BOOKED"];
const CATEGORIES = ["Sweet Stuff", "Savory Stuff", "Drinks"];
const IMAGES = [
  "/images/cinnamon-roll.png", "/images/biscuits-gravy.png", "/images/coffee.png",
  "/images/hero-truck.png", "/images/story-trailer.png", "/logo.jpg", "/menu-board.jpg",
];
const EMPTY_STOP = { day: "Tuesday", status: "SERVING", time: "", location: "", address: "", map_query: "" };
const EMPTY_ITEM = { slug: "", name: "", category: "Sweet Stuff", price: "$4", price_note: "", tag: "New", desc: "", image: "/images/cinnamon-roll.png" };
const TOKEN_KEY = "cb_admin_token";

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="cb-label">{label}</label>
    <input data-testid={testid} className="cb-input !py-2 !text-sm" {...props} />
  </div>
);

const ScheduleEditor = () => {
  const [stops, setStops] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => { fetchSchedule().then(setStops); }, []);

  const update = (i, k, v) => setStops((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveSchedule(stops);
      qc.invalidateQueries({ queryKey: ["schedule"] });
      toast.success("Truck route updated — it's live on the site!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!stops) return <p className="font-display font-semibold uppercase animate-pulse" data-testid="route-loading">Loading route...</p>;

  return (
    <div className="space-y-6" data-testid="route-editor">
      {stops.map((s, i) => (
        <div key={i} data-testid={`admin-stop-card-${i}`} className="cb-card p-5 relative">
          <button
            data-testid={`admin-stop-delete-${i}`}
            onClick={() => setStops((x) => x.filter((_, idx) => idx !== i))}
            aria-label="Delete stop"
            className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-cb-black bg-cb-pink text-white flex items-center justify-center hover:bg-cb-black transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pr-10">
            <div>
              <label className="cb-label">Day</label>
              <select data-testid={`admin-stop-day-${i}`} className="cb-input !py-2 !text-sm" value={s.day} onChange={(e) => update(i, "day", e.target.value)}>
                {DAYS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="cb-label">Status</label>
              <select data-testid={`admin-stop-status-${i}`} className="cb-input !py-2 !text-sm" value={s.status} onChange={(e) => update(i, "status", e.target.value)}>
                {STATUSES.map((st) => <option key={st}>{st}</option>)}
              </select>
            </div>
            <Field label="Time Slot" testid={`admin-stop-time-${i}`} placeholder="7:00 AM - 11:30 AM" value={s.time} onChange={(e) => update(i, "time", e.target.value)} />
            <Field label="Location Name" testid={`admin-stop-location-${i}`} placeholder="Downtown Farmers Market" value={s.location} onChange={(e) => update(i, "location", e.target.value)} />
            <Field label="Street Address" testid={`admin-stop-address-${i}`} placeholder="Main St & 2nd Ave" value={s.address} onChange={(e) => update(i, "address", e.target.value)} />
            <Field label="Map Search (include city!)" testid={`admin-stop-map-${i}`} placeholder="e.g. 400 Riverside Pkwy, Springfield" value={s.map_query} onChange={(e) => update(i, "map_query", e.target.value)} />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <button data-testid="admin-add-stop-btn" onClick={() => setStops((s) => [...s, { ...EMPTY_STOP }])} className="cb-btn cb-btn-teal !text-sm">
          <Plus className="w-4 h-4" /> Add Stop
        </button>
        <button data-testid="admin-save-route-btn" onClick={save} disabled={saving} className="cb-btn cb-btn-pink !text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Route"}
        </button>
      </div>
    </div>
  );
};

const MenuEditor = () => {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => { fetchMenu().then(setItems); }, []);

  const update = (i, k, v) => setItems((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const withSlugs = items.map((it) => ({
        ...it,
        slug: it.slug || it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      }));
      await adminSaveMenu(withSlugs);
      setItems(withSlugs);
      qc.invalidateQueries({ queryKey: ["menu"] });
      toast.success("Menu updated — it's live on the site!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <p className="font-display font-semibold uppercase animate-pulse" data-testid="menu-loading-admin">Loading menu...</p>;

  return (
    <div className="space-y-6" data-testid="menu-editor">
      {items.map((it, i) => (
        <div key={i} data-testid={`admin-item-card-${i}`} className="cb-card p-5 relative">
          <button
            data-testid={`admin-item-delete-${i}`}
            onClick={() => setItems((x) => x.filter((_, idx) => idx !== i))}
            aria-label="Delete item"
            className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-cb-black bg-cb-pink text-white flex items-center justify-center hover:bg-cb-black transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pr-10">
            <Field label="Item Name" testid={`admin-item-name-${i}`} value={it.name} onChange={(e) => update(i, "name", e.target.value)} />
            <Field label="Price" testid={`admin-item-price-${i}`} placeholder="$4" value={it.price} onChange={(e) => update(i, "price", e.target.value)} />
            <Field label="Price Note" testid={`admin-item-note-${i}`} placeholder="4 for $15" value={it.price_note} onChange={(e) => update(i, "price_note", e.target.value)} />
            <div>
              <label className="cb-label">Category</label>
              <select data-testid={`admin-item-category-${i}`} className="cb-input !py-2 !text-sm" value={it.category} onChange={(e) => update(i, "category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Badge Tag" testid={`admin-item-tag-${i}`} placeholder="Bestseller" value={it.tag} onChange={(e) => update(i, "tag", e.target.value)} />
            <div>
              <label className="cb-label">Image</label>
              <div className="flex items-center gap-2">
                <img src={it.image} alt="" className="w-10 h-10 rounded-xl border-2 border-cb-black object-cover shrink-0" />
                <select data-testid={`admin-item-image-${i}`} className="cb-input !py-2 !text-sm" value={it.image} onChange={(e) => update(i, "image", e.target.value)}>
                  {IMAGES.map((img) => <option key={img}>{img}</option>)}
                </select>
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="cb-label">Description</label>
              <textarea data-testid={`admin-item-desc-${i}`} rows={2} className="cb-input !py-2 !text-sm resize-none" value={it.desc} onChange={(e) => update(i, "desc", e.target.value)} />
            </div>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <button data-testid="admin-add-item-btn" onClick={() => setItems((s) => [...s, { ...EMPTY_ITEM }])} className="cb-btn cb-btn-teal !text-sm">
          <Plus className="w-4 h-4" /> Add Menu Item
        </button>
        <button data-testid="admin-save-menu-btn" onClick={save} disabled={saving} className="cb-btn cb-btn-pink !text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Menu"}
        </button>
      </div>
    </div>
  );
};

const EMPTY_EVENT = { date: "", name: "", place: "" };

const EventsEditor = () => {
  const [events, setEvents] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => { fetchEvents().then(setEvents); }, []);

  const update = (i, k, v) => setEvents((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveEvents(events);
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success("Events updated — they're live on the site!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!events) return <p className="font-display font-semibold uppercase animate-pulse" data-testid="events-loading-admin">Loading events...</p>;

  return (
    <div className="space-y-6" data-testid="events-editor">
      {events.map((ev, i) => (
        <div key={i} data-testid={`admin-event-card-${i}`} className="cb-card p-5 relative">
          <button
            data-testid={`admin-event-delete-${i}`}
            onClick={() => setEvents((x) => x.filter((_, idx) => idx !== i))}
            aria-label="Delete event"
            className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-cb-black bg-cb-pink text-white flex items-center justify-center hover:bg-cb-black transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="grid sm:grid-cols-3 gap-4 pr-10">
            <Field label="Date Label" testid={`admin-event-date-${i}`} placeholder="Sat, Aug 8" value={ev.date} onChange={(e) => update(i, "date", e.target.value)} />
            <Field label="Event Name" testid={`admin-event-name-${i}`} placeholder="Sunnydale Summer Fest" value={ev.name} onChange={(e) => update(i, "name", e.target.value)} />
            <Field label="Place" testid={`admin-event-place-${i}`} placeholder="Sunnydale Park" value={ev.place} onChange={(e) => update(i, "place", e.target.value)} />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <button data-testid="admin-add-event-btn" onClick={() => setEvents((s) => [...s, { ...EMPTY_EVENT }])} className="cb-btn cb-btn-teal !text-sm">
          <Plus className="w-4 h-4" /> Add Event
        </button>
        <button data-testid="admin-save-events-btn" onClick={save} disabled={saving} className="cb-btn cb-btn-pink !text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Events"}
        </button>
      </div>
    </div>
  );
};

const EMPTY_REVIEW = { name: "", role: "", rating: 5, comment: "" };

const ReviewsEditor = () => {
  const [reviews, setReviews] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  useEffect(() => { fetchReviews().then(setReviews); }, []);

  const update = (i, k, v) => setReviews((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveReviews(reviews);
      qc.invalidateQueries({ queryKey: ["reviews"] });
      toast.success("Reviews updated — they're live on the homepage!");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!reviews) return <p className="font-display font-semibold uppercase animate-pulse" data-testid="reviews-loading-admin">Loading reviews...</p>;

  return (
    <div className="space-y-6" data-testid="reviews-editor">
      {reviews.map((r, i) => (
        <div key={i} data-testid={`admin-review-card-${i}`} className="cb-card p-5 relative">
          <button
            data-testid={`admin-review-delete-${i}`}
            onClick={() => setReviews((x) => x.filter((_, idx) => idx !== i))}
            aria-label="Delete review"
            className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-cb-black bg-cb-pink text-white flex items-center justify-center hover:bg-cb-black transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="grid sm:grid-cols-3 gap-4 pr-10">
            <Field label="Customer Name" testid={`admin-review-name-${i}`} placeholder="Sarah Jenkins" value={r.name} onChange={(e) => update(i, "name", e.target.value)} />
            <Field label="Role / Label" testid={`admin-review-role-${i}`} placeholder="Local Foodie & Mom" value={r.role} onChange={(e) => update(i, "role", e.target.value)} />
            <div>
              <label className="cb-label">Star Rating</label>
              <select data-testid={`admin-review-rating-${i}`} className="cb-input !py-2 !text-sm" value={r.rating} onChange={(e) => update(i, "rating", e.target.value)}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <div className="sm:col-span-3">
              <label className="cb-label">Review</label>
              <textarea data-testid={`admin-review-comment-${i}`} rows={2} className="cb-input !py-2 !text-sm resize-none" placeholder="The cinnamon rolls are incredible!" value={r.comment} onChange={(e) => update(i, "comment", e.target.value)} />
            </div>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        <button data-testid="admin-add-review-btn" onClick={() => setReviews((s) => [...s, { ...EMPTY_REVIEW }])} className="cb-btn cb-btn-teal !text-sm">
          <Plus className="w-4 h-4" /> Add Review
        </button>
        <button data-testid="admin-save-reviews-btn" onClick={save} disabled={saving} className="cb-btn cb-btn-pink !text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Reviews"}
        </button>
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("route");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    adminMe()
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, boss!");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div data-testid="admin-page" className="bg-cb-cream halftone-light min-h-[80vh] py-14 sm:py-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <span className="cb-badge bg-cb-yellow text-cb-black mb-4 inline-block">Owner HQ</span>
          <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl" data-testid="admin-heading">
            Crazy Buns <span className="text-cb-pink">Admin</span>
          </h1>
          <p className="mt-3 font-bold text-cb-black/60 text-sm sm:text-base">Update the truck route and menu — no code, no fuss.</p>
        </div>

        {checking ? (
          <p className="text-center font-display font-semibold uppercase animate-pulse">Checking your key...</p>
        ) : !token ? (
          <form onSubmit={login} data-testid="admin-login-form" className="cb-card max-w-md mx-auto p-8 -rotate-1 hover:rotate-0">
            <div className="w-14 h-14 mx-auto rounded-full border-3 border-cb-black bg-cb-pink flex items-center justify-center shadow-hard-sm mb-5">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-4">
              <div>
                <label className="cb-label" htmlFor="ad-email">Email</label>
                <input id="ad-email" data-testid="admin-login-email" type="email" required className="cb-input" placeholder="you@crazybuns.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <label className="cb-label" htmlFor="ad-pass">Password</label>
                <input id="ad-pass" data-testid="admin-login-password" type="password" required className="cb-input" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              {error && <p data-testid="admin-login-error" className="text-cb-pink font-extrabold text-sm">{error}</p>}
              <button type="submit" data-testid="admin-login-submit" disabled={busy} className="cb-btn cb-btn-black w-full disabled:opacity-60">
                {busy ? "Unlocking..." : "Unlock Admin"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
              <button
                data-testid="admin-tab-route"
                onClick={() => setTab("route")}
                className={`cb-btn !text-sm ${tab === "route" ? "cb-btn-black" : "cb-btn-white"}`}
              >
                <Truck className="w-4 h-4" /> Truck Route
              </button>
              <button
                data-testid="admin-tab-menu"
                onClick={() => setTab("menu")}
                className={`cb-btn !text-sm ${tab === "menu" ? "cb-btn-black" : "cb-btn-white"}`}
              >
                <UtensilsCrossed className="w-4 h-4" /> Menu
              </button>
              <button
                data-testid="admin-tab-events"
                onClick={() => setTab("events")}
                className={`cb-btn !text-sm ${tab === "events" ? "cb-btn-black" : "cb-btn-white"}`}
              >
                <CalendarDays className="w-4 h-4" /> Events
              </button>
              <button
                data-testid="admin-tab-reviews"
                onClick={() => setTab("reviews")}
                className={`cb-btn !text-sm ${tab === "reviews" ? "cb-btn-black" : "cb-btn-white"}`}
              >
                <Star className="w-4 h-4" /> Reviews
              </button>
              <button data-testid="admin-logout-btn" onClick={logout} className="cb-btn cb-btn-yellow !text-sm">
                <LogOut className="w-4 h-4" /> Log Out
              </button>
            </div>
            {tab === "route" ? <ScheduleEditor /> : tab === "menu" ? <MenuEditor /> : tab === "events" ? <EventsEditor /> : <ReviewsEditor />}
          </div>
        )}
      </div>
    </div>
  );
}
